import React, { useEffect, useRef } from 'react';
import { Trophy, Award, Sparkles, X, Share2, CheckCircle2 } from 'lucide-react';

const ChallengeCelebrationModal = ({ challenge, onClose }) => {
    const canvasRef = useRef(null);

    // Animación de Confeti nativa en Canvas (ligera y fluida)
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        let animationFrameId;

        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const colors = ['#f59e0b', '#10b981', '#3b82f6', '#ec4899', '#8b5cf6', '#fc4c02'];
        const particles = Array.from({ length: 80 }).map(() => ({
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height - canvas.height,
            size: Math.random() * 8 + 4,
            color: colors[Math.floor(Math.random() * colors.length)],
            speedY: Math.random() * 3 + 2,
            speedX: Math.random() * 2 - 1,
            rotation: Math.random() * 360,
            rotationSpeed: Math.random() * 10 - 5
        }));

        const render = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            particles.forEach((p) => {
                p.y += p.speedY;
                p.x += p.speedX;
                p.rotation += p.rotationSpeed;

                if (p.y > canvas.height) {
                    p.y = -20;
                    p.x = Math.random() * canvas.width;
                }

                ctx.save();
                ctx.translate(p.x, p.y);
                ctx.rotate((p.rotation * Math.PI) / 180);
                ctx.fillStyle = p.color;
                ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
                ctx.restore();
            });

            animationFrameId = requestAnimationFrame(render);
        };

        render();

        const handleResize = () => {
            if (canvas) {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            cancelAnimationFrame(animationFrameId);
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    if (!challenge) return null;

    const getSportIcon = (sport) => {
        const s = (sport || '').toLowerCase();
        if (s.includes('ciclismo') || s.includes('cycling')) return '🚴';
        if (s.includes('running') || s.includes('correr')) return '🏃';
        if (s.includes('natación') || s.includes('swim')) return '🏊';
        if (s.includes('fuerza')) return '💪';
        return '🏆';
    };

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justify: 'center',
            backgroundColor: 'rgba(5, 10, 20, 0.85)',
            backdropFilter: 'blur(12px)',
            padding: '1.5rem'
        }}>
            {/* Canvas de Confeti de Fondo */}
            <canvas
                ref={canvasRef}
                style={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    zIndex: 1
                }}
            />

            {/* Modal Principal de Celebración */}
            <div style={{
                position: 'relative',
                zIndex: 2,
                width: '100%',
                maxWidth: '460px',
                background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.98), rgba(15, 23, 42, 0.99))',
                borderRadius: '28px',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.7), 0 0 40px rgba(245, 158, 11, 0.15)',
                padding: '2.5rem 2rem',
                textAlign: 'center',
                overflow: 'hidden'
            }}>
                {/* Botón de Cierre */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '1.25rem',
                        right: '1.25rem',
                        background: 'rgba(255, 255, 255, 0.06)',
                        border: 'none',
                        color: 'rgba(255, 255, 255, 0.6)',
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justify: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                    }}
                >
                    <X size={18} />
                </button>

                {/* Insignia / Trofeo Brillante */}
                <div style={{
                    position: 'relative',
                    width: '100px',
                    height: '100px',
                    margin: '0 auto 1.5rem auto',
                    display: 'flex',
                    alignItems: 'center',
                    justify: 'center',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(245, 158, 11, 0.25) 0%, rgba(245, 158, 11, 0.05) 70%)',
                    boxShadow: '0 0 30px rgba(245, 158, 11, 0.3)'
                }}>
                    <div style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                        display: 'flex',
                        alignItems: 'center',
                        justify: 'center',
                        boxShadow: '0 8px 24px rgba(245, 158, 11, 0.4)'
                    }}>
                        <Trophy size={42} color="#ffffff" />
                    </div>
                    <div style={{
                        position: 'absolute',
                        bottom: '-4px',
                        right: '-4px',
                        background: '#10b981',
                        borderRadius: '50%',
                        padding: '4px',
                        display: 'flex',
                        border: '3px solid #0f172a'
                    }}>
                        <CheckCircle2 size={16} color="#ffffff" />
                    </div>
                </div>

                {/* Título de Felicitación */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginBottom: '0.5rem' }}>
                    <Sparkles size={20} color="#f59e0b" />
                    <span style={{ fontSize: '0.85rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: '#f59e0b' }}>
                        ¡Reto Conseguido!
                    </span>
                    <Sparkles size={20} color="#f59e0b" />
                </div>

                <h2 style={{
                    margin: '0 0 0.75rem 0',
                    fontSize: '1.75rem',
                    fontWeight: 800,
                    color: '#ffffff',
                    lineHeight: 1.2
                }}>
                    {getSportIcon(challenge.sport)} {challenge.title || `${challenge.sport === 'All' || challenge.sport === 'Cualquiera' ? 'Reto Global' : challenge.sport}`}
                </h2>

                <p style={{
                    fontSize: '0.95rem',
                    color: 'rgba(255, 255, 255, 0.7)',
                    margin: '0 0 1.75rem 0',
                    lineHeight: 1.5
                }}>
                    ¡Enhorabuena! Has alcanzado con éxito tu objetivo de <strong style={{ color: '#10b981' }}>{challenge.target} {challenge.unit === 'km' ? 'km' : challenge.unit === 'count' ? 'sesiones' : challenge.metric === 'elevation' ? 'm D+' : challenge.unit}</strong>.
                </p>

                {/* Métricas destacadas del logro */}
                <div style={{
                    background: 'rgba(255, 255, 255, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '16px',
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '1rem',
                    marginBottom: '2rem'
                }}>
                    <div>
                        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Progreso Final</div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#10b981' }}>100%</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Objetivo</div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#ffffff' }}>
                            {challenge.target} {challenge.unit === 'km' ? 'km' : challenge.unit === 'count' ? 'sesiones' : 'unidades'}
                        </div>
                    </div>
                </div>

                {/* Acciones */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <button
                        onClick={onClose}
                        className="btn-primary"
                        style={{
                            width: '100%',
                            padding: '0.9rem',
                            fontSize: '1rem',
                            fontWeight: 700,
                            borderRadius: '14px',
                            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                            border: 'none',
                            color: '#ffffff',
                            cursor: 'pointer',
                            boxShadow: '0 8px 20px rgba(245, 158, 11, 0.3)',
                            transition: 'all 0.2s'
                        }}
                    >
                        ¡Genial, a por el siguiente!
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChallengeCelebrationModal;
